<template>
  <div class="sb-sub">
    <!-- TEMP: mock state switcher. Remove when the API drives `stateKey`. See docs/subscription-page-states.md -->
    <div class="sb-switch">
      <span>Mock states</span>
      <button
        v-for="(st, key) in states"
        :key="key"
        type="button"
        :class="{ on: stateKey === key }"
        @click="stateKey = key"
      >{{ st.tab }}</button>
    </div>

    <div class="sb-page">
      <div class="sb-top">
        <div>
          <h1>Subscription</h1>
          <p>{{ s.sub }}</p>
        </div>
        <span class="sb-pill" :class="s.pill[1]">{{ s.pill[0] }}</span>
      </div>

      <div class="sb-grid">
        <!-- LEFT COLUMN -->
        <div class="sb-col">
          <div class="sb-card">
            <div class="sb-card-h" style="background: var(--navy)">
              Your plan<span class="tag">{{ s.pill[0] }}</span>
            </div>
            <div class="sb-rows">
              <div class="sb-row"><span class="k">Account ID</span><span class="v">{{ accountEmail }}</span></div>
              <div class="sb-row"><span class="k">Subscription plan</span><span class="v">{{ s.plan }}</span></div>
              <div class="sb-row"><span class="k">Renewal date</span><span class="v">{{ s.renewal }}</span></div>
            </div>
          </div>

          <div v-if="s.payment" class="sb-card">
            <div class="sb-card-h" style="background: var(--teal)">Payment</div>
            <div class="sb-rows">
              <div v-for="r in s.payment" :key="r[0]" class="sb-row">
                <span class="k">{{ r[0] }}</span><span class="v">{{ r[1] }}</span>
              </div>
            </div>
          </div>

          <div class="sb-card" :class="{ glow: s.glow }">
            <div class="sb-card-h" style="background: var(--blue)">
              Download<span v-if="s.glow" class="tag">+10 credits · +30 days</span>
            </div>
            <div class="sb-dl">
              <canvas ref="qr" width="100" height="100"></canvas>
              <p>Download the app in seconds. Simply scan the QR code to access it on the App Store.</p>
            </div>
            <a class="sb-store" href="#" @click.prevent>
              <i class="fa fa-download"></i>Download on the App Store
            </a>
          </div>
        </div>

        <!-- RIGHT PANEL -->
        <div class="sb-panel">
          <div v-if="s.banner" class="sb-banner" :class="s.banner.variant">
            <span class="ic"><i class="fa" :class="s.banner.icon"></i></span>
            <div><b>{{ s.banner.title }}</b><span>{{ s.banner.text }}</span></div>
          </div>

          <h2>{{ s.head }}</h2>
          <p class="lead">{{ s.lead }}</p>

          <div class="sb-two">
            <!-- Usage: subscribed -->
            <div v-if="s.counts" class="sb-card sb-usage">
              <div class="sb-card-h" style="background: var(--gold)">Usage this month</div>
              <div class="sb-rows">
                <div class="sb-row"><span class="k">Status</span><span class="sb-badge">Active</span></div>
                <div class="sb-row"><span class="k">Renewal date</span><span class="v">{{ s.renewal }}</span></div>
              </div>
              <div class="sb-bars">
                <div v-for="c in s.counts" :key="c[0]" class="sb-bar" style="margin-bottom: 24px">
                  <div class="t">
                    <b><i class="dot" :style="{ background: COL[c[1]] }"></i>{{ c[0] }}</b>
                    <span>{{ c[2] }} created · Unlimited</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Usage: free / expired -->
            <div v-else class="sb-card sb-usage" :class="{ dim: s.dim }">
              <div class="sb-card-h" style="background: var(--gold)">Credit usage</div>
              <div class="sb-rows">
                <div class="sb-row">
                  <span class="k">Status</span>
                  <span class="sb-badge" :class="s.status[1]">{{ s.status[0] }}</span>
                </div>
                <div class="sb-row">
                  <span class="k">{{ s.dim ? 'Credits expired on' : 'Credit expiry date' }}</span>
                  <span class="v">{{ s.expiry }}</span>
                </div>
              </div>
              <div class="sb-bars">
                <div v-for="b in s.bars" :key="b[0]" class="sb-bar">
                  <div class="t">
                    <b>{{ b[0] }}<span v-if="b[4]" class="bonus">{{ b[4] }}</span></b>
                    <span :class="{ full: isFull(b) }">{{ b[2] }} of {{ b[3] }} used{{ isFull(b) ? ' · used up' : '' }}</span>
                  </div>
                  <div class="track"><i :style="{ width: Math.round((b[2] / b[3]) * 100) + '%', background: COL[b[1]] }"></i></div>
                  <div v-if="b[5]" class="hint">{{ b[5] }}</div>
                </div>
              </div>
              <div v-if="s.history" class="sb-history">
                <i class="fa fa-info-circle"></i><span>{{ s.history }}</span>
              </div>
            </div>

            <!-- Right card: offer / web / apple -->
            <div v-if="s.right === 'offer'" class="sb-card sb-offer">
              <div class="sb-card-h" style="background: var(--gold)">Smoothbalance includes</div>
              <div class="body">
                <div class="price"><b>£3.99</b><span>/month</span></div>
                <div class="feats">
                  <div v-for="f in FEATS" :key="f[0]" class="feat">
                    <i :style="{ background: COL[f[1]] }"></i>{{ f[0] }}
                    <em :style="{ background: f[2], color: f[3] }">Unlimited</em>
                  </div>
                </div>
                <div class="toggle"><div class="on">Monthly</div><div>Annual</div></div>
                <button type="button" class="cta" @click="onSubscribe">Subscribe · £3.99/month</button>
                <div class="legal">Auto-renews until cancelled. <a>Terms</a> · <a>Privacy</a></div>
              </div>
            </div>

            <div v-else class="sb-card">
              <div class="sb-card-h" style="background: var(--navy)">Your subscription includes</div>
              <div class="body">
                <div class="price"><b>£3.99</b><span>/month</span></div>
                <div class="feats">
                  <div v-for="f in FEATS" :key="f[0]" class="feat">
                    <i :style="{ background: COL[f[1]] }"></i>{{ f[0] }}
                    <em :style="{ background: f[2], color: f[3] }">Unlimited</em>
                  </div>
                </div>
                <div class="legal left">Auto-renews on dd/mm/yyyy until cancelled.</div>
                <template v-if="s.right === 'web'">
                  <button type="button" class="btn-o" @click="onManage">Manage subscription</button>
                  <div class="legal">Change your card or cancel at any time.</div>
                </template>
                <div v-else class="note">
                  <b>Manage in the App Store.</b> You subscribed in the iPhone app, so changes and cancellation are
                  made on your iPhone: Settings › Apple ID › Subscriptions.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * STATIC DESIGN ONLY - no API yet.
 * `stateKey` (toggled by the mock switcher) stands in for what the API will decide.
 * Every state's data lives in STATES below; see docs/subscription-page-states.md for the
 * condition -> state mapping to implement once the API exists.
 */
const COL = { inv: "var(--green)", quo: "var(--orange)", exp: "var(--blue)" };
const FEATS = [
  ["Invoices", "inv", "#DDF3F1", "#12877F"],
  ["Quotations or Estimates", "quo", "#FBF3DC", "#9C7400"],
  ["Expense tracking", "exp", "#DDF3F1", "#12877F"],
];
const HISTORY = "Usage history: includes the 10 invoices you created in Invoice Book.";
const BARS = [
  ["Invoices", "inv", 13, 20],
  ["Quotations / Estimates", "quo", 4, 10],
  ["Expenses", "exp", 7, 10],
];
const COUNTS = [
  ["Invoices", "inv", 23],
  ["Quotations / Estimates", "quo", 9],
  ["Expenses", "exp", 14],
];
const SUBSCRIBED = {
  sub: "Unlimited invoices, quotations and expenses.",
  pill: ["Monthly plan", "pro"],
  plan: "Monthly · £3.99",
  renewal: "dd/mm/yyyy",
  head: "You're subscribed",
  lead: "Create as many invoices, quotations and expenses as you need, across the web and mobile app.",
  counts: COUNTS,
};
const FREE_BASE = {
  sub: "Invoices, quotations and expenses are on free credits.",
  pill: ["Free plan", ""],
  plan: "Free",
  renewal: "Not applicable",
  status: ["Active", ""],
  expiry: "dd/mm/yyyy",
  history: HISTORY,
  right: "offer",
};

const STATES = {
  free: {
    ...FREE_BASE,
    tab: "1. Free · bonus added",
    head: "You've been using all three areas",
    lead: "You're cruising nicely. Keep up the great work with your invoices, quotations, and expenses across the web and mobile app.",
    bars: [["Invoices", "inv", 13, 20, "+10 bonus"], BARS[1], BARS[2]],
  },
  solo: {
    ...FREE_BASE,
    tab: "2. Solo user · bonus not yet claimed",
    banner: {
      icon: "fa-gift",
      title: "Get 10 extra invoice credits and 30 more days",
      text: "Download the Smoothbalance app on your iPhone and sign in. Your extra credits are added automatically.",
    },
    head: "Welcome from Invoice Book",
    lead: "Your data has moved across. Quotations and expenses are ready to use on your free credits.",
    bars: [
      ["Invoices", "inv", 10, 10, null, "Download the Smoothbalance app to add 10 more."],
      ["Quotations / Estimates", "quo", 0, 10],
      ["Expenses", "exp", 0, 10],
    ],
    glow: true,
  },
  usedup: {
    ...FREE_BASE,
    tab: "3. One feature used up",
    head: "You've used all your free invoices",
    lead: "Your existing invoices still open and export. Quotations and expenses still work on your free credits. Subscribe to create new invoices.",
    bars: [["Invoices", "inv", 20, 20, "+10 bonus", "New invoices are paused until you subscribe."], BARS[1], BARS[2]],
  },
  expired: {
    ...FREE_BASE,
    tab: "4. All credits expired",
    sub: "Your free credits have expired.",
    pill: ["Credits expired", "warn"],
    banner: {
      icon: "fa-clock",
      variant: "grey",
      title: "Your account is read-only",
      text: "Everything you created still opens and exports. Subscribe to create new invoices, quotations and expenses.",
    },
    head: "Your free credits have expired",
    lead: "You still had credits left, but the free period has ended.",
    status: ["Expired", "exp"],
    bars: BARS,
    dim: true,
  },
  web: {
    ...SUBSCRIBED,
    tab: "5. Subscribed on the web",
    payment: [
      ["Method", "Visa ending 4417"],
      ["Transaction date", "dd/mm/yyyy"],
      ["Billed through", "This website"],
    ],
    right: "web",
  },
  apple: {
    ...SUBSCRIBED,
    tab: "6. Subscribed through Apple",
    payment: [
      ["Method", "Apple ID"],
      ["Transaction date", "dd/mm/yyyy"],
      ["Billed through", "App Store"],
    ],
    right: "apple",
  },
};

export default {
  name: "Subscription",
  data() {
    return {
      stateKey: "free", // TODO(api): derive from API response instead of the mock switcher
      states: STATES,
      COL,
      FEATS,
    };
  },
  computed: {
    s() {
      return this.states[this.stateKey];
    },
    accountEmail() {
      const user = this.$store?.getters?.["auth/getUserData"];
      return user?.email || "allbizltd@gmail.com";
    },
  },
  mounted() {
    this.drawQR();
  },
  updated() {
    // QR canvas is re-created by v-if/v-else swaps rarely; redraw is cheap and idempotent
    this.drawQR();
  },
  methods: {
    isFull(b) {
      return b[2] >= b[3] && !this.s.dim;
    },
    // TODO(api): wire to setting/getSubscriptionCheckout (previous implementation in git history)
    onSubscribe() {},
    // TODO(api): open Stripe customer portal
    onManage() {},
    // Placeholder QR pattern only. Replace with the real App Store campaign-link QR.
    drawQR() {
      const c = this.$refs.qr;
      if (!c) return;
      const x = c.getContext("2d");
      const n = 25;
      const z = 4;
      let seed = 11;
      const r = () => (seed = (seed * 9301 + 49297) % 233280) / 233280;
      x.clearRect(0, 0, c.width, c.height);
      x.fillStyle = "#1B2130";
      for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) if (r() > 0.55) x.fillRect(i * z, j * z, z, z);
      [[0, 0], [n - 7, 0], [0, n - 7]].forEach(([a, b]) => {
        x.fillStyle = "#fff"; x.fillRect(a * z, b * z, 7 * z, 7 * z);
        x.fillStyle = "#1B2130"; x.fillRect(a * z, b * z, 7 * z, 7 * z);
        x.fillStyle = "#fff"; x.fillRect((a + 1) * z, (b + 1) * z, 5 * z, 5 * z);
        x.fillStyle = "#1B2130"; x.fillRect((a + 2) * z, (b + 2) * z, 3 * z, 3 * z);
      });
    },
  },
};
</script>

<style scoped>
.sb-sub {
  --navy: #3c4178; --teal: #1fbbb2; --blue: #4a9bb5; --gold: #d6aa33; --gold-ink: #9c7400; --gold-soft: #fbf3dc;
  --green: #2db87a; --orange: #f05a28; --mint: #e3f4ea; --mint-ink: #1c8a5a;
  --bg: #f6f7f9; --card: #fff; --ink: #1b2130; --ink-2: #5f6673; --ink-3: #9aa0ab; --line: #e7e9ed; --track: #eef0f3;
  background: var(--bg);
  color: var(--ink);
  font-size: 14px;
  text-align: left;
}
.sb-sub *, .sb-sub *::before, .sb-sub *::after { box-sizing: border-box; }

/* mock-only switcher */
.sb-switch { position: sticky; top: 0; z-index: 5; background: #1b2130; color: #fff; padding: 10px 24px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.sb-switch span { font-size: 12px; opacity: 0.6; margin-right: 6px; text-transform: uppercase; letter-spacing: 0.06em; }
.sb-switch button { font-weight: 600; font-size: 13px; color: #fff; background: rgba(255, 255, 255, 0.1); border: 0; border-radius: 99px; padding: 7px 14px; cursor: pointer; }
.sb-switch button.on { background: var(--gold); color: #1b2130; }

.sb-page { padding: 22px 24px 28px; max-width: 1500px; margin: 0 auto; }
.sb-top { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; padding-bottom: 18px; border-bottom: 1px solid var(--line); margin-bottom: 22px; }
.sb-top h1 { font-size: 30px; font-weight: 800; letter-spacing: -0.02em; margin: 0; }
.sb-top p { margin: 10px 0 0; color: var(--ink-2); }
.sb-pill { font-size: 13px; font-weight: 700; padding: 7px 14px; border-radius: 99px; background: #ddf3f1; color: #12877f; white-space: nowrap; }
.sb-pill.warn { background: var(--gold-soft); color: var(--gold-ink); }
.sb-pill.pro { background: var(--mint); color: var(--mint-ink); }

.sb-grid { display: grid; grid-template-columns: 400px 1fr; gap: 28px; align-items: start; }
.sb-col { display: flex; flex-direction: column; gap: 18px; }
.sb-card { background: var(--card); border: 1px solid var(--line); border-radius: 14px; overflow: hidden; }
.sb-card-h { padding: 16px 22px; color: #fff; font-weight: 700; font-size: 15px; display: flex; justify-content: space-between; align-items: center; }
.sb-card-h .tag { font-size: 12px; font-weight: 700; background: rgba(255, 255, 255, 0.22); padding: 4px 11px; border-radius: 99px; }
.sb-rows { padding: 4px 22px; }
.sb-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 14px 0; border-top: 1px solid var(--line); }
.sb-row:first-child { border-top: 0; }
.sb-row .k { color: var(--ink-2); }
.sb-row .v { font-weight: 700; word-break: break-all; text-align: right; }
.sb-dl { padding: 20px; display: flex; gap: 16px; align-items: center; }
.sb-dl canvas { width: 120px; height: 120px; border: 1px solid var(--line); border-radius: 12px; padding: 10px; background: #fff; flex: none; }
.sb-dl p { font-weight: 600; font-size: 15px; line-height: 1.5; margin: 0; }
.sb-store { margin: 0 20px 20px; height: 50px; border-radius: 10px; background: #000; color: #fff; display: flex; align-items: center; justify-content: center; gap: 10px; font-weight: 600; font-size: 15px; text-decoration: none; }
.sb-card.glow { border: 2px solid var(--gold); box-shadow: 0 0 0 5px var(--gold-soft); }

.sb-panel { background: var(--card); border: 1px solid var(--line); border-radius: 14px; padding: 30px; }
.sb-panel h2 { font-size: 21px; font-weight: 800; letter-spacing: -0.01em; margin: 0; }
.sb-panel .lead { margin: 10px 0 0; color: var(--ink-2); line-height: 1.6; max-width: 640px; }
.sb-banner { display: flex; gap: 16px; align-items: center; background: var(--gold-soft); border: 1px solid #ebd79a; border-radius: 12px; padding: 16px 20px; margin-bottom: 24px; }
.sb-banner .ic { flex: none; width: 44px; height: 44px; border-radius: 50%; background: var(--gold); display: grid; place-items: center; color: #fff; font-size: 18px; }
.sb-banner b { display: block; font-size: 16px; color: #5e4700; }
.sb-banner span { color: #7a6220; line-height: 1.5; }
.sb-banner.grey { background: #f3f4f6; border-color: var(--line); }
.sb-banner.grey .ic { background: var(--ink-3); }
.sb-banner.grey b { color: var(--ink); }
.sb-banner.grey span { color: var(--ink-2); }
.sb-two { display: grid; grid-template-columns: 1fr 1fr; gap: 26px; margin-top: 24px; align-items: start; }

.sb-usage .sb-rows { border-bottom: 1px solid var(--line); }
.sb-badge { font-size: 12px; font-weight: 700; padding: 5px 12px; border-radius: 99px; background: #ddf3f1; color: #12877f; }
.sb-badge.exp { background: #fde8e2; color: #c2411c; }
.sb-bars { padding: 26px 22px 8px; }
.sb-bar { margin-bottom: 30px; }
.sb-bar .t { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 12px; }
.sb-bar .t b { font-size: 15px; display: flex; align-items: center; gap: 8px; }
.sb-bar .t > span { color: var(--ink-2); font-size: 13px; }
.sb-bar .t > span.full { color: #c2411c; font-weight: 700; }
.sb-bar .dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.bonus { font-size: 11px; font-weight: 700; background: var(--gold-soft); color: var(--gold-ink); padding: 3px 8px; border-radius: 99px; }
.track { height: 6px; border-radius: 99px; background: var(--track); overflow: hidden; }
.track i { display: block; height: 100%; border-radius: 99px; }
.hint { font-size: 12.5px; color: #c2411c; margin-top: 9px; line-height: 1.45; }
.sb-history { display: flex; gap: 8px; align-items: flex-start; margin: 0 22px 22px; padding: 11px 14px; background: #f3f4f6; border-radius: 10px; font-size: 13px; color: var(--ink-2); line-height: 1.45; }
.sb-history i { flex: none; margin-top: 2px; }
.dim .track i { background: #c5c9d0 !important; }
.dim .sb-bar .t b { color: var(--ink-2); }

.sb-offer { border: 2px solid var(--gold); }
.sb-two .body { padding: 22px; }
.price { display: flex; align-items: baseline; gap: 8px; }
.price b { font-size: 40px; font-weight: 800; letter-spacing: -0.03em; }
.price span { color: var(--ink-2); }
.feats { margin-top: 8px; }
.feat { display: flex; align-items: center; gap: 12px; padding: 13px 0; border-bottom: 1px solid var(--line); font-weight: 700; font-size: 15px; }
.feat i { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.feat em { margin-left: auto; font-style: normal; font-size: 12px; font-weight: 700; padding: 5px 11px; border-radius: 99px; }
.toggle { display: grid; grid-template-columns: 1fr 1fr; background: var(--mint); border-radius: 8px; padding: 3px; margin-top: 22px; font-weight: 700; text-align: center; }
.toggle div { padding: 10px; border-radius: 6px; color: var(--mint-ink); }
.toggle div.on { background: #5dc88f; color: #fff; }
.cta { width: 100%; border: 0; margin-top: 12px; height: 48px; border-radius: 8px; background: var(--gold); color: #fff; font-weight: 700; font-size: 15px; cursor: pointer; }
.legal { margin-top: 12px; text-align: center; font-size: 12.5px; color: var(--ink-2); line-height: 1.6; }
.legal.left { text-align: left; margin-top: 16px; }
.legal a { color: var(--ink-2); text-decoration: underline; cursor: pointer; }
.btn-o { width: 100%; background: transparent; margin-top: 18px; height: 46px; border-radius: 8px; border: 1.5px solid var(--navy); color: var(--navy); font-weight: 700; cursor: pointer; }
.note { margin-top: 18px; padding: 14px 16px; background: #f3f4f6; border-radius: 10px; font-size: 13.5px; color: var(--ink-2); line-height: 1.55; }
.note b { color: var(--ink); }

@media (max-width: 1100px) {
  .sb-grid { grid-template-columns: 1fr; }
}
@media (max-width: 800px) {
  .sb-two { grid-template-columns: 1fr; }
  .sb-page { padding: 16px; }
  .sb-panel { padding: 18px; }
  .sb-top h1 { font-size: 24px; }
}
</style>
